import { useAppDispatch, useAppSelector } from './state'
import { setManagedResourceAuthorizations, setResourceAuthorization, setResourceAuthorizations } from '../state/slices/resource_authorization_slice'
import {
  changeCurrentResourceAuthorization as changeCurrentResourceAuthorizationApi,
  getResourceAuthorizationsManagedByUser as getResourceAuthorizationsManagedByUserApi,
  addResourceAuthorization as addResourceAuthorizationApi,
  verifyAddResourceAuthorization as verifyAddResourceAuthorizationApi,
  deleteResourceAuthorization as deleteResourceAuthorizationApi,
  getResourceAuthorization as getResourceAuthorizationApi
} from '../api/resource_authorization'
import { useCallback, useState } from 'react'
import { setUser } from '../state/slices/user_slice'
import { setConceptClaim } from '../state/slices/claims_slice'

export const useResourceAuthorization = () => {
  const dispatch = useAppDispatch()

  const [isLoading, setIsLoading] = useState(false)

  const managedResourceAuthorizations = useAppSelector(state => state.resourceAuthorization.managedResourceAuthorizations)
  const pendingResourceAuthorizations = useAppSelector(state => state.resourceAuthorization.pendingResourceAuthorizations)

  const getResourceAuthorization = async () => {
    try {
      const response = await getResourceAuthorizationApi()

      if (response.data) {
        dispatch(setResourceAuthorization(response.data.user.currentResourceAuthorization))
        dispatch(setResourceAuthorizations(response.data.user.resourceAuthorizations || []))
      }

      return response
    } catch (error) {
      return null
    }
  }

  const getResourceAuthorizationsManagedByUser = async (userId: number) => {
    setIsLoading(true)
    try {
      const response = await getResourceAuthorizationsManagedByUserApi(userId)

      if (response.data && response.data.length > 0) {
        const managedResourceAuthorizations = [...response.data[0].resourceAuthorizations, response.data[0].currentResourceAuthorization]

        dispatch(setManagedResourceAuthorizations({
          pendingResourceAuthorizations: response.data[0].pendingResourceAuthorizations || [],
          managedResourceAuthorizations: managedResourceAuthorizations
        }))
      }

      return response
    } catch (error) {
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const changeCurrentResourceAuthorization = async (relationNumber: number) => {
    try {
      const response = await changeCurrentResourceAuthorizationApi(relationNumber)

      if (response.data) {
        dispatch(setUser(response.data.user))
        dispatch(setResourceAuthorizations(response.data.user.resourceAuthorizations || []))
        dispatch(setResourceAuthorization(response.data.user.currentResourceAuthorization))

        // Clear concept claim when changing resource authorization
        dispatch(setConceptClaim(null))
      }

      return response
    } catch (error) {
      return null
    }
  }

  const addResourceAuthorization = async (combiNumber: number, email: string) => await addResourceAuthorizationApi(combiNumber, email)

  const verifyAddResourceAuthorization = async (email: string, relationNumber: number, addResourceAuthToken: string) => {
    const response = await verifyAddResourceAuthorizationApi(email, relationNumber, addResourceAuthToken)

    if (response.data) {
      dispatch(setResourceAuthorizations(response.data?.user.resourceAuthorizations || []))
      dispatch(setResourceAuthorization(response.data?.user.currentResourceAuthorization))
    }

    return response
  }

  const deleteResourceAuthorization = async (resourceAuthorizationId: number) => {
    try {
      const response = await deleteResourceAuthorizationApi(resourceAuthorizationId)

      if (response.data) {
        dispatch(setResourceAuthorizations(response.data?.user.resourceAuthorizations || []))
        dispatch(setResourceAuthorization(response.data?.user.currentResourceAuthorization))
      }

      return response
    } catch (error) {
      return null
    }
  }

  const getManagedResourceAuthorizationById = useCallback((id: number) => managedResourceAuthorizations?.find(auth => auth.id === id), [managedResourceAuthorizations])

  return {
    getResourceAuthorizationsManagedByUser,
    changeCurrentResourceAuthorization,
    addResourceAuthorization,
    verifyAddResourceAuthorization,
    deleteResourceAuthorization,
    getResourceAuthorization,

    // Getters
    getManagedResourceAuthorizationById,
    managedResourceAuthorizations,
    pendingResourceAuthorizations,
    isLoading
  }
}

export default useResourceAuthorization
